import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { KeyRound, Loader2, CheckCircle2, Sparkles } from 'lucide-react';

/** Pagina aperta dal link ricevuto via email: si sceglie la nuova password. */
const ResetPasswordPage = () => {
  const { API } = useAuth();
  const { branding } = useBranding();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSend = password.length >= 8 && confirm === password && !!token;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Non è stato possibile cambiare la password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md space-y-8">
        <div className="flex items-center gap-2">
          {branding.logo_url
            ? <img src={`${API}${branding.logo_url}`} alt="" className="h-8 w-auto" />
            : <Sparkles className="w-6 h-6 text-primary" />}
          <span className="font-semibold font-outfit">{branding.brand_name || 'Salon Suite'}</span>
        </div>

        {done ? (
          <div className="space-y-3" data-testid="reset-done">
            <CheckCircle2 className="w-10 h-10 text-primary" />
            <h1 className="text-2xl md:text-3xl font-bold font-outfit">Fatto</h1>
            <p className="text-muted-foreground">
              Password aggiornata. Ti porto alla pagina di accesso...
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <span className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-primary" />
              </span>
              <h1 className="text-2xl md:text-3xl font-bold font-outfit">Scegli la nuova password</h1>
              <p className="text-muted-foreground">
                {token
                  ? 'Almeno 8 caratteri. Chi è collegato con la vecchia password verrà scollegato.'
                  : 'Link incompleto: riapri quello che hai ricevuto via email.'}
              </p>
            </div>

            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="new-password">Nuova password</Label>
                <Input id="new-password" type="password" value={password} className="h-11"
                  onChange={(e) => setPassword(e.target.value)} data-testid="new-password-input" />
                {tooShort && (
                  <p className="text-xs text-destructive" data-testid="too-short-hint">
                    Servono almeno 8 caratteri.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Ripeti la password</Label>
                <Input id="confirm-password" type="password" value={confirm} className="h-11"
                  onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input" />
                {mismatch && (
                  <p className="text-xs text-destructive" data-testid="mismatch-hint">
                    Le due password non coincidono.
                  </p>
                )}
              </div>

              {error && (
                <p className="text-sm text-destructive bg-destructive/10 p-3 rounded-md" data-testid="reset-error">
                  {error}
                </p>
              )}

              <Button type="submit" className="w-full h-11" disabled={busy || !canSend}
                data-testid="reset-password-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Salva la nuova password
              </Button>
            </form>

            <button type="button" onClick={() => navigate('/login')} data-testid="reset-back-btn"
              className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Torna all'accesso
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
